import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Building2, Briefcase, Trash2, ArrowUpRight, Sparkles } from "lucide-react";

const STATUS_COLORS = {
  "À contacter": "bg-slate-100 text-slate-700 border-slate-200",
  "Candidature envoyée": "bg-blue-50 text-blue-700 border-blue-100",
  "Relance": "bg-amber-50 text-amber-700 border-amber-100",
  "Entretien": "bg-violet-50 text-violet-700 border-violet-100",
  "Accepté": "bg-emerald-50 text-emerald-700 border-emerald-100",
  "Refusé": "bg-rose-50 text-rose-700 border-rose-100",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApps(data);
    } catch {
      toast.error("Impossible de charger les candidatures.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      setApps((a) => a.filter((x) => x.id !== id));
      toast.success("Candidature supprimée.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const filtered = filter === "all" ? apps : apps.filter((a) => a.status === filter);

  const stats = {
    total: apps.length,
    entretiens: apps.filter((a) => a.status === "Entretien").length,
    envoyees: apps.filter((a) => ["Candidature envoyée", "Relance", "Entretien", "Accepté"].includes(a.status)).length,
    acceptees: apps.filter((a) => a.status === "Accepté").length,
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Nav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Mes candidatures
            </h1>
            <p className="mt-1 text-slate-500">Suis, relance et améliore chaque candidature.</p>
          </div>
          <Button onClick={() => navigate("/new")} className="bg-blue-600 hover:bg-blue-700 rounded-full px-5 h-11 shadow-sm shadow-blue-600/20" data-testid="dashboard-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle candidature
          </Button>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Candidatures", value: stats.total },
            { label: "Envoyées", value: stats.envoyees },
            { label: "Entretiens", value: stats.entretiens },
            { label: "Acceptées", value: stats.acceptees },
          ].map((s) => (
            <Card key={s.label} className="p-5 rounded-2xl border-slate-200 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-medium">{s.label}</div>
              <div className="mt-2 font-jakarta text-3xl font-extrabold text-slate-900">{s.value}</div>
            </Card>
          ))}
        </div>

        {/* Filter */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-500">
            {filtered.length} candidature{filtered.length > 1 ? "s" : ""}
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[220px] bg-white rounded-full h-10" data-testid="status-filter">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.keys(STATUS_COLORS).map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="mt-4">
          {loading ? (
            <div className="py-16 text-center text-slate-400">Chargement…</div>
          ) : filtered.length === 0 ? (
            <Card className="p-12 rounded-2xl border-dashed border-slate-300 bg-white text-center">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-blue-600">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-jakarta text-lg font-semibold text-slate-900">Aucune candidature pour l'instant</h3>
              <p className="mt-1 text-slate-500 text-sm">Crée ta première candidature adaptée en quelques minutes.</p>
              <Button onClick={() => navigate("/new")} className="mt-6 bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="dashboard-empty-cta">
                <Plus className="h-4 w-4 mr-1.5" /> Créer ma première candidature
              </Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filtered.map((a) => (
                <Card key={a.id} className="p-5 rounded-2xl border-slate-200 hover:border-blue-200 hover:shadow-md transition-all bg-white" data-testid={`app-card-${a.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`rounded-full font-medium border ${STATUS_COLORS[a.status]}`}>{a.status}</Badge>
                        <Badge variant="outline" className="rounded-full text-slate-500 border-slate-200">{a.contract_type}</Badge>
                        <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 font-jakarta text-lg font-semibold text-slate-900 truncate">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        {a.position || "Poste non précisé"}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-sm text-slate-500 truncate">
                        <Building2 className="h-3.5 w-3.5" />
                        {a.company || "Entreprise non précisée"}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {a.analysis?.score != null && (
                        <div className="text-right">
                          <div className="text-xs uppercase tracking-widest text-slate-400">Score</div>
                          <div className="font-jakarta text-2xl font-extrabold text-blue-600">{a.analysis.score}%</div>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Button asChild size="sm" variant="outline" className="rounded-full">
                          <Link to={`/candidature/${a.id}`} data-testid={`open-app-${a.id}`}>
                            Ouvrir <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-rose-600" data-testid={`delete-app-${a.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette candidature ?</AlertDialogTitle>
                              <AlertDialogDescription>Cette action est définitive et supprimera l'analyse et les documents générés.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="delete-cancel">Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(a.id)} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm">Supprimer</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
